import type { WhatsAppIntegration } from 'src/api/generated';
import {
  IntegrationConnectionStatus,
  WhatsAppSolutionAssociationInconclusive,
  WhatsAppSolutionAssociationVerificationInconclusiveReason,
} from 'src/api/generated';
import { classifyWhatsAppIntegrationOnboardingSafety } from 'src/modules/shared/utils/integrations/whatsapp-integration-onboarding-safety.util';
import { isRecord } from 'src/modules/shared/utils/types.util';
import type { WhatsAppConnectionPresentation } from '../types/whatsapp-connection-presentation.type';

const getLifecycleRecoveryAction = (
  failureCode: string | undefined,
  isUnsafe: boolean
) => {
  if (failureCode === 'WhatsAppAccountReconnectRequired') {
    return isUnsafe ? 'support' : 'reconnect';
  }
  if (failureCode === 'WhatsAppAccountVerificationRequired') {
    return isUnsafe ? 'support' : 'retry_setup';
  }
  return undefined;
};

/**
 * Chooses a safe customer next step using structured connection/evidence fields.
 * Import status is excluded: retrying setup cannot retry imports.
 * @param integration - Saved account, absent when its details cannot be matched.
 * @returns Presentation state; permission checks remain with the caller and API.
 */
export const getWhatsAppConnectionPresentation = (
  integration: WhatsAppIntegration | undefined
): WhatsAppConnectionPresentation => {
  if (!integration || !isRecord(integration.integrationFields)) {
    return 'support';
  }
  const failureCode = integration.connectionHealth?.lastFailureCode;
  if (failureCode === 'WhatsAppOffboardingPending') {
    return 'disconnect_pending';
  }
  // This API code also covers offboarding, which may need device-side steps.
  // Do not guess the event from provider prose or offer a blind setup retry.
  if (failureCode === 'WhatsAppAccountLifecyclePending') {
    return 'meta_change';
  }

  const safety = classifyWhatsAppIntegrationOnboardingSafety(integration);
  const verification =
    integration.integrationFields.solutionAssociationVerification;
  const lifecycleAction = getLifecycleRecoveryAction(
    failureCode,
    safety.isUnsafe
  );
  if (lifecycleAction) return lifecycleAction;
  if (
    safety.isUnsafe &&
    verification?.status !==
      WhatsAppSolutionAssociationInconclusive.status.INCONCLUSIVE
  ) {
    return 'support';
  }

  if (
    failureCode === 'MetaPhoneStatus:DISCONNECTED' ||
    /^(OAuthException|GraphMethodException):(190|10|200)(:|$)/.test(
      failureCode ?? ''
    ) ||
    /^(UnauthorizedError|ForbiddenError|NotFoundError)(:|$)/.test(
      failureCode ?? ''
    )
  ) {
    return 'reconnect';
  }

  if (safety.isUnsafe) {
    return verification?.status ===
      WhatsAppSolutionAssociationInconclusive.status.INCONCLUSIVE &&
      verification.reason ===
        WhatsAppSolutionAssociationVerificationInconclusiveReason.META_REQUEST_FAILED
      ? 'retry_setup'
      : 'support';
  }
  if (failureCode === 'WhatsAppOnboardingIncomplete') return 'retry_setup';
  if (
    integration.connectionStatus &&
    integration.connectionStatus !== IntegrationConnectionStatus.ACTIVE
  ) {
    return 'retry_setup';
  }
  return 'healthy';
};
